import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Heart, AlertCircle, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCartStore } from "@/store/cartStore";
import { useFavoritesStore } from "@/store/favoritesStore";
import { toast } from "sonner";
import { Product } from "@/types/product";
import { useProductDetail } from "@/contexts/ProductDetailContext";

interface ProductCardProps extends Product {
  tags?: string[];
}

export function ProductCard({ id, title, price, image, prescription, tags = [], category }: ProductCardProps) {
  const { addItem } = useCartStore();
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();
  const { openProductDetail } = useProductDetail();
  const favorite = id ? isFavorite(id) : false;

  const handleAddToCart = () => {
    if (prescription) {
      // We'll still show a toast notification but won't block the user from adding to cart
      toast.info("This medication requires a prescription. You'll need to provide one during checkout.");
    }

    const item = {
      id: id || title.toLowerCase().replace(/\s+/g, '-'),
      title,
      price,
      image,
      category,
      prescription,
    };
    
    addItem(item);
    
    toast.success(`${title} has been added to your medication tray`);
  };

  const handleToggleFavorite = () => {
    const productId = id || title.toLowerCase().replace(/\s+/g, '-');
    
    if (favorite) {
      removeFavorite(productId);
      toast.success(`${title} has been removed from your favorites`);
    } else {
      const productToAdd = {
        id: productId,
        title,
        price,
        image,
        category,
        prescription
      };
      addFavorite(productToAdd);
      toast.success(`${title} has been added to your favorites`);
    }
  };

  const handleLearnMore = (e: React.MouseEvent) => {
    e.preventDefault();
    openProductDetail({
      id: id || title.toLowerCase().replace(/\s+/g, '-'),
      title,
      price,
      image,
      category,
      prescription
    });
  };

  return (
    <Card className="group overflow-hidden hover:shadow-md transition-shadow duration-200 flex flex-col max-h-[320px] md:max-h-[360px]">
      <CardHeader className="p-0 relative">
        <div className="h-[140px] xs:h-[120px] overflow-hidden bg-gray-100 dark:bg-gray-800">
          <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        </div>
        <Button 
          size="icon" 
          variant="ghost" 
          className="absolute top-2 right-2 rounded-full bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-900 h-6 w-6 xs:h-5 xs:w-5"
          onClick={handleToggleFavorite}
        >
          <Heart className={`h-3 w-3 xs:h-2.5 xs:w-2.5 ${favorite ? 'fill-red-500 text-red-500' : ''}`} />
        </Button>
        
        {prescription && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute top-2 left-2 bg-yellow-500/80 p-1 rounded-full">
                  <AlertCircle className="h-3 w-3 xs:h-2.5 xs:w-2.5 text-white" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Prescription required</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardHeader>
      <CardContent className="flex-grow p-2 xs:p-1.5 sm:p-3">
        <div className="mb-1">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="mr-1 mb-1 text-xs xs:text-[10px]">
              {tag}
            </Badge>
          ))}
        </div>
        <h3 className="font-medium text-sm xs:text-xs sm:text-base mb-1 line-clamp-1">{title}</h3>
        <div className="flex items-center justify-between">
          <span className="font-semibold text-base xs:text-sm">₦{price.toLocaleString()}</span>
        </div>
      </CardContent>
      <CardFooter className="p-2 xs:p-1.5 sm:p-3 pt-0 gap-1 xs:gap-0.5">
        <div className="grid grid-cols-2 gap-1 xs:gap-0.5 w-full">
          <Button 
            className="rounded-full text-xs xs:text-[10px] h-7 xs:h-6 px-2"
            variant="secondary"
            onClick={handleLearnMore}
          >
            <Eye className="mr-1 h-3 w-3 xs:h-2 xs:w-2" />
            Details
          </Button>
          <Button 
            className="rounded-full text-xs xs:text-[10px] h-7 xs:h-6 px-2"
            onClick={handleAddToCart}
          >
            Add to Tray
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
